import { Component, OnInit, signal, computed, WritableSignal, Signal, inject, OnDestroy } from '@angular/core';
import { UserService } from '../services/entities/user.service';
import { RoleService } from '../services/entities/role.service';
import { User } from '@app/types';
import { Role } from '@app/types/role.type';
import { MaterialModule } from '@app/material.module';
import { CommonModule } from '@angular/common';

import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';
import { Subject, takeUntil, debounceTime, tap } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { RoleDialogComponent } from './role-dialog/role-dialog.component';
import { UserDetailDialogComponent } from './user-detail-dialog/user-detail-dialog.component';


@Component({
  selector: 'app-user',
  standalone: true,
  imports: [CommonModule, MaterialModule, ReactiveFormsModule],
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss']
})
export class UserComponent implements OnInit, OnDestroy {
  private userService = inject(UserService);
  public roleService = inject(RoleService); // Public for template access
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);

  usersSignal: Signal<User[]>;
  rolesSignal: Signal<Role[]>;
  filteredUsersSignal: Signal<User[]>;
  paginatedUsersSignal: Signal<User[]>;
  isLoadingSignal: Signal<boolean>; // Global loading from service

  // Local signals
  searchControl = new FormControl('');
  private searchSignal: WritableSignal<string> = signal('');
  isFilteringSignal: WritableSignal<boolean> = signal(false); // Local spinner for search

  // Pagination signals
  pageIndex: WritableSignal<number> = signal(0);
  pageSize: WritableSignal<number> = signal(10);

  displayedColumns: string[] = ['name', 'email', 'roles', 'status', 'createdAt', 'actions'];

  private destroy$ = new Subject<void>();

  constructor() {
    this.isLoadingSignal = this.userService.isLoadingSignal;
    this.usersSignal = this.userService.users;
    this.rolesSignal = this.roleService.rolesSignal;

    this.filteredUsersSignal = computed(() => {
      const users = this.usersSignal();
      const searchTerm = this.searchSignal().toLowerCase();

      if (!searchTerm) {
        return users;
      }
      return users.filter(user => {
        const serialized = JSON.stringify(user).toLowerCase();
        return serialized.includes(searchTerm);
      });
    });

    this.paginatedUsersSignal = computed(() => {
      const users = this.filteredUsersSignal();
      const startIndex = this.pageIndex() * this.pageSize();
      return users.slice(startIndex, startIndex + this.pageSize());
    });
  }

  ngOnInit(): void {
    // Debounced search
    this.searchControl.valueChanges.pipe(
      takeUntil(this.destroy$),
      tap(() => this.isFilteringSignal.set(true)),
      debounceTime(800)
    ).subscribe(value => {
      this.searchSignal.set(value || '');
      this.pageIndex.set(0);
      this.isFilteringSignal.set(false);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handlePageEvent(e: PageEvent) {
    this.pageIndex.set(e.pageIndex);
    this.pageSize.set(e.pageSize);
  }

  goBack(): void {
    this.router.navigate(['/home']);
  }

  limpiarFiltros(): void {
    this.searchControl.setValue('');
    this.pageIndex.set(0);
  }

  forzarRefrescoCompleto(): void {
    this.userService.loadUsers();
    this.roleService.loadRoles().subscribe();
    this.snackBar.open('Refrescando usuarios y roles...', 'Cerrar', { duration: 2000 });
  }

  // Role management methods
  openRoleDialog(role?: Role): void {
    const dialogRef = this.dialog.open(RoleDialogComponent, {
      width: '600px',
      data: role || null
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.snackBar.open('Rol guardado correctamente', 'Cerrar', { duration: 3000 });
      }
    });
  }

  getUserRoleNames(userId: string): string {
    const roles = this.rolesSignal();
    const userRoles = roles.filter(role => role.users && role.users.includes(userId));
    return userRoles.map(r => r.name).join(', ') || 'Sin roles';
  }

  openUserDetail(user: User): void {
    this.dialog.open(UserDetailDialogComponent, {
      width: '560px',
      data: user
    });
  }

  deleteRole(role: Role): void {
    if (confirm(`¿Eliminar el rol "${role.name}"? Esta acción no se puede deshacer.`)) {
      this.roleService.deleteRole(role.id).subscribe({
        next: () => this.snackBar.open('Rol eliminado correctamente', 'Cerrar', { duration: 3000 }),
        error: () => this.snackBar.open('Error al eliminar el rol', 'Cerrar', { duration: 3000 })
      });
    }
  }
}
